/**
 * Black Box — utilities. All must fail silent (never break the host page).
 */

/**
 * Identify an element WITHOUT capturing any value.
 * Prefers stable identifiers: data-bb > id > name > tag + first class.
 */
/**
 * Is the element actually rendered? offsetParent is null for display:none — but
 * ALSO for every position:fixed element, so full-screen success pages / modals
 * read as hidden. getClientRects() is empty only when the element (or an
 * ancestor) is display:none, and works for fixed elements too.
 */
export function isRendered(el) {
  try { return !!el && el.getClientRects().length > 0; } catch (_) { return false; }
}

export function elId(el) {
  if (!el || el === document || el === window) return 'window';
  try {
    if (el.getAttribute && el.getAttribute('data-bb')) return el.getAttribute('data-bb');
    if (el.id) return '#' + el.id;
    if (el.name) return '[name=' + el.name + ']';
    const tag = (el.tagName || '').toLowerCase();
    const cls = el.classList && el.classList.length ? '.' + el.classList[0] : '';
    return tag + cls;
  } catch (_) {
    return 'unknown';
  }
}

// Decorative inline nodes a click often lands on INSIDE a real control (a button
// whose label is a <span>, an icon <svg>/<path>). Only these climb to their
// control — everything else (inputs, divs, the elements themselves) is left as-is.
const CLICK_PASSTHROUGH = /^(span|svg|path|use|g|i|b|strong|em|small|img|picture|source|figure|figcaption)$/;

/**
 * Resolve a raw click target to the element its selector should describe. A tap
 * on the <span> inside <button id="toStep2"> would otherwise record as a generic
 * "span" and replay would click the wrong node (e.g. a modal's close button).
 * Climbs from a decorative inner node to the nearest identified/interactive
 * ancestor; capped in depth and falls back to the original element.
 */
export function clickTarget(el) {
  try {
    if (!el || el.nodeType !== 1) return el;
    if (!CLICK_PASSTHROUGH.test((el.tagName || '').toLowerCase())) return el;
    let node = el.parentElement, depth = 0;
    while (node && node.nodeType === 1 && depth < 4) {
      if (node.getAttribute && node.getAttribute('data-bb')) return node;
      if (node.id) return node;
      const tag = (node.tagName || '').toLowerCase();
      if (tag === 'button' || tag === 'a' || tag === 'summary' || tag === 'label') return node;
      const role = node.getAttribute && node.getAttribute('role');
      if (role === 'button' || role === 'link') return node;
      node = node.parentElement; depth++;
    }
  } catch (_) {}
  return el;
}

/** Match an element against a logical-name -> selector map; return the name. */
export function matchField(el, fieldMap) {
  try {
    for (const name in fieldMap) {
      if (el.matches && el.matches(fieldMap[name])) return name;
    }
  } catch (_) {}
  return null;
}

/** Coarse device class from user agent (no fingerprinting). */
export function parseUA() {
  const ua = navigator.userAgent || '';
  let browser = 'Other';
  if (/SamsungBrowser/i.test(ua)) browser = 'Samsung Internet';
  else if (/Edg/i.test(ua)) browser = 'Edge';
  else if (/OPR|Opera/i.test(ua)) browser = 'Opera';
  else if (/Chrome/i.test(ua)) browser = 'Chrome';
  else if (/Firefox/i.test(ua)) browser = 'Firefox';
  else if (/Safari/i.test(ua)) browser = 'Safari';

  let os = 'Other';
  if (/Android/i.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';
  else if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Mac OS X/i.test(ua)) os = 'macOS';
  else if (/Linux/i.test(ua)) os = 'Linux';

  const mobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  const device = mobile ? 'Mobile' : 'Desktop';

  return { device, browser, os };
}

/** Connection type where the Network Information API is available. */
export function connectionType() {
  try {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return (c && c.effectiveType) || null;
  } catch (_) {
    return null;
  }
}

/** Market guess from <html lang> / path / navigator (best-effort, overridable). */
export function guessMarket() {
  try {
    const path = location.pathname.toLowerCase();
    const m = path.match(/\/([a-z]{2})(\/|$)/);
    if (m) return m[1];
    const htmlLang = (document.documentElement.getAttribute('lang') || '').slice(0, 2);
    if (htmlLang) return htmlLang;
  } catch (_) {}
  return null;
}

/**
 * Auto-derive a site label: a declared og:site_name / application-name if the
 * page has one, otherwise the hostname (incl. port, so it distinguishes pages
 * served on different ports locally). Overridable via init({ site }).
 */
export function siteName() {
  try {
    const og = document.querySelector('meta[property="og:site_name"]');
    if (og && og.content) return og.content.slice(0, 80);
    const an = document.querySelector('meta[name="application-name"]');
    if (an && an.content) return an.content.slice(0, 80);
  } catch (_) {}
  try { return location.host || location.hostname || null; } catch (_) { return null; }
}

export function language() {
  try {
    return (navigator.language || 'en').slice(0, 2);
  } catch (_) {
    return 'en';
  }
}

/** Random session id; avoids Date-based collisions across tabs. */
export function makeSessionId() {
  const rnd = Math.random().toString(36).slice(2, 10);
  const rnd2 = Math.random().toString(36).slice(2, 6);
  return 'bb_' + rnd + rnd2;
}

/** True if a query param equals a value (activation gate). */
export function hasFlag(param, value) {
  try {
    return new URLSearchParams(location.search).get(param) === value;
  } catch (_) {
    return false;
  }
}
